import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { UserAsset } from '@models/dashboard.model';
import {
  AddressInfo,
  ExistingAssetDetails,
  OrderDetailsAdditionalInfo,
  ShoppingCartData,
  SourceOrderDetails
} from '@models/shopping-cart.model';
import {
  ACTIVITY_3_ASSET_CARD_FIELDS,
  FieldErrorMessage,
  SimFormOutputEvent
} from '@models/shopping.model';
import { TranslationPipe } from '@pipes/translation/translation.pipe';
import { ActivityStateService } from '@services/activity/state/activity-state.service';
import { NotificationsService } from '@services/notifications/notifications.service';
import { ShoppingService } from '@services/shopping/shopping.service';
import { TranslationService } from '@services/translation/translation.service';
import { ViewService } from 'tangoe-design-system';

import { AssetCardComponent } from '../asset/card/asset-card.component';
import { DeviceToActivateComponent } from '../device-to-activate/device-to-activate.component';
import { DeviceToUpgradeComponent } from '../device-to-upgrade/device-to-upgrade.component';
import { MyLineComponent } from '../my-line/my-line.component';
import { PersonAssignmentComponent } from '../person-assignment/person-assignment.component';
import { RevisedFeaturesReadonlyComponent } from '../revised-features-readonly/revised-features-readonly.component';
import { TransferDetailsReadonlyComponent } from '../transfer-details-readonly/transfer-details-readonly.component';
import { TravelDetailsReadonlyComponent } from '../travel-details-readonly/travel-details-readonly.component';

@Component({
  selector: 'ts-selected-asset',
  templateUrl: './selected-asset.component.html',
  imports: [
    CommonModule,
    TranslationPipe,
    AssetCardComponent,
    DeviceToActivateComponent,
    DeviceToUpgradeComponent,
    MyLineComponent,
    PersonAssignmentComponent,
    RevisedFeaturesReadonlyComponent,
    TransferDetailsReadonlyComponent,
    TravelDetailsReadonlyComponent
  ]
})
export class SelectedAssetComponent implements OnChanges {
  @Input() order_type_id: number;
  @Input() asset_details: ExistingAssetDetails;
  @Input() additional_info: OrderDetailsAdditionalInfo;
  @Input() shipping_address: AddressInfo;
  @Input() shopping_cart: ShoppingCartData;
  @Input() validation_errors: FieldErrorMessage[];
  @Input() page_key: string;
  @Output() form_valid: EventEmitter<boolean> = new EventEmitter();
  @Output() trigger_update_fields: EventEmitter<{
    cart: ShoppingCartData;
    key?: string;
    update_cart?: boolean;
  }> = new EventEmitter();
  review: boolean = false;
  confirmation: boolean = false;
  summary: boolean = false;
  finalize: boolean = false;
  device_to_activate_fields: string[] = ACTIVITY_3_ASSET_CARD_FIELDS;

  constructor(
    private activity_state: ActivityStateService,
    private notifications_svc: NotificationsService,
    private shopping_svc: ShoppingService,
    private translation_svc: TranslationService,
    private view_svc: ViewService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['page_key']) {
      this.finalize = this.page_key === 'finalize';
      this.review = this.page_key === 'review';
      this.confirmation = this.page_key === 'confirmation';
      this.summary = this.page_key === 'summary';
    }
  }

  get card_wrap(): boolean {
    return this.review || this.finalize;
  }

  get asset_config(): {
    header: string;
    asset_fields: string[];
    asset: UserAsset;
    card_wrap: boolean;
  } {
    let header: string = '';
    let asset_fields: string[] = [];

    switch (this.order_type_id) {
      case 4:
        header = this.translation_svc.translate('line_to_disconnect');
        asset_fields = ['plan', 'sim_number', 'add_on_features', 'contract_end_date'];
        break;

      case 5:
        header = this.translation_svc.translate('line_to_port');
        asset_fields = ['plan', 'contract_end_date', 'add_on_features'];
        break;

      case 7:
        header = this.translation_svc.translate('line_to_suspend');
        asset_fields = ['plan', 'sim_number', 'add_on_features', 'contract_end_date'];
        break;

      case 10:
        header = this.translation_svc.translate('line_to_unsuspend');
        asset_fields = ['plan', 'sim_number', 'add_on_features', 'contract_end_date'];
        break;

      case 11:
        header = this.translation_svc.translate('selected_line');
        asset_fields = ['plan', 'plan_features', 'add_on_features'];
        break;

      case 14:
        header = this.translation_svc.translate('selected_device');
        asset_fields = this.finalize
          ? ['imei', 'imei2', 'serial_number', 'eid', 'sim_compatibility']
          : ['imei', 'imei2', 'serial_number'];
        break;

      case 15:
      case 16:
        header = this.translation_svc.translate('selected_line');
        asset_fields = ['plan', 'plan_features', 'add_on_features'];
        break;

      case 19:
        header = this.translation_svc.translate('selected_line');
        asset_fields = ['plan', 'add_on_features', 'contract_end_date', 'carrier_account'];
        break;

      default:
        break;
    }

    return {
      header: header,
      asset_fields: asset_fields,
      asset: this.asset_details.existing_asset,
      card_wrap: this.review || this.finalize
    };
  }

  handleSimForm(sim_value: SimFormOutputEvent): void {
    this.shopping_cart.device_identifier = sim_value.identifier;
    this.shopping_cart.order_sim = sim_value.type === 'new' ? true : false;
    this.shopping_cart.new_sim = sim_value.type === 'own' ? sim_value.value : undefined;
    this.shopping_cart.esim_activation = sim_value.type === 'esim' ? true : undefined;
    this.shopping_cart.new_eid = sim_value.type === 'esim' ? sim_value.value : undefined;

    this.shopping_svc.sendShoppingItems(this.shopping_cart, false).subscribe({
      next: (res: ShoppingCartData) => {
        this.trigger_update_fields.emit({ cart: this.shopping_cart, key: 'activate-options' });
        this.form_valid.emit(sim_value.valid);
      },
      error: (err: HttpErrorResponse) => {}
    });
  }

  updateReplacementPerson(e: { cart: ShoppingCartData; on_change?: boolean }): void {
    e.cart.on_change_field = e.cart.replacement_person
      ? JSON.parse('{"name":"replacement_person"}')
      : undefined;

    this.activity_state.setParameter('replacement_person', e.cart.replacement_person);
    this.notifications_svc.orderNotifications(
      e.cart.orderTypeId,
      '',
      e.cart.replacement_person?.id
    );

    if (e.on_change)
      this.trigger_update_fields.emit({ cart: e.cart, key: 'global', update_cart: true });

    this.form_valid.emit(Boolean(e.cart.replacement_person));
    this.view_svc.resetScrollPosition();
  }
}
